import React from "react";
import { Alert, Button, Checkbox, Descriptions, Space, Table, Typography, message } from "antd";
import { useTranslation } from "react-i18next";
import {
  downloadCompliancePassport,
  downloadCompliancePassportVersion,
  generatePassport,
} from "../../api";
import type { ComplianceCaseDetail, CompliancePassport, DeforestationAssessment } from "../types";
import { DeforestationComparisonPanel } from "./DeforestationComparisonPanel";
import { PassportVersionCompare } from "./PassportVersionCompare";
import { EudrTermHint } from "./EudrTermHint";
import { pushNotification } from "../ux/notificationStore";
import { MobileDataCards } from "./MobileDataCards";
import { useNarrowViewport } from "../ux/useNarrowViewport";
type Props = {
  caseData: ComplianceCaseDetail;
  onUpdated: () => void;
};

export function PassportPanel({ caseData, onUpdated }: Props) {
  const { t } = useTranslation();
  const isNarrow = useNarrowViewport("md");
  const [loading, setLoading] = React.useState(false);
  const [downloadLoading, setDownloadLoading] = React.useState(false);
  const [versionDownloading, setVersionDownloading] = React.useState<number | null>(null);
  const [acknowledged, setAcknowledged] = React.useState(false);
  const passed = caseData.latest_validation?.passed ?? false;
  const passport = caseData.passport;
  const preview = passport?.passport_json as Record<string, unknown> | undefined;
  const versions = (caseData.passports?.length ? caseData.passports : passport ? [passport] : []) as CompliancePassport[];

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      <Alert type="warning" showIcon message={t("compliance.passport.legalDisclaimer")} />
      <Checkbox checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)}>
        {t("compliance.passport.acknowledgeResponsibility")}
      </Checkbox>
      <Button
        type="primary"
        disabled={!passed || !acknowledged}
        loading={loading}
        onClick={async () => {
          setLoading(true);
          try {
            await generatePassport(caseData.id);
            message.success(t("compliance.passport.generated"));
            pushNotification({
              kind: "passport_generated",
              title: t("compliance.notifications.passportGenerated"),
              caseId: caseData.id,
            });
            onUpdated();
          } catch {
            message.error(t("compliance.passport.generateFailed"));
          } finally {
            setLoading(false);
          }
        }}
      >
        {t("compliance.passport.generate")}
      </Button>
      {!passed && (
        <Typography.Text type="secondary">{t("compliance.passport.mustPassValidation")}</Typography.Text>
      )}
      {passed && !acknowledged && (
        <Typography.Text type="secondary">{t("compliance.passport.mustAcknowledge")}</Typography.Text>
      )}
      {!preview && passed && (
        <Typography.Paragraph type="secondary">
          {t("compliance.passport.previewBeforeGenerate")}
        </Typography.Paragraph>
      )}
      {preview && (
        <>
          <Descriptions bordered size="small" column={1} title={t("compliance.passport.previewTitle")}>
            <Descriptions.Item label={t("compliance.passport.commodity")}>
              {String(preview.commodity ?? "")}
            </Descriptions.Item>
            <Descriptions.Item label={t("compliance.passport.traceabilityTitle")}>
              {(() => {
                const tr = (preview.traceability_summary || preview.traceability) as
                  | Record<string, unknown>
                  | undefined;
                if (!tr) return t("common.dash");
                const bits = [
                  tr.supplier_name,
                  tr.supplier_country,
                  tr.destination_country,
                  tr.contract_reference,
                  tr.client_org_name,
                ].filter(Boolean);
                return bits.length ? bits.map(String).join(" · ") : t("common.dash");
              })()}
            </Descriptions.Item>
            <Descriptions.Item
              label={
                <Space size={4}>
                  <span>{t("compliance.passport.riskScore")}</span>
                  <EudrTermHint term="riskScore" testId="passport-risk-hint" />
                </Space>
              }
            >
              {String((preview.risk_assessment as { risk_score?: number })?.risk_score ?? t("common.dash"))}
            </Descriptions.Item>
            {(() => {
              const ra = preview.risk_assessment as { deforestation?: DeforestationAssessment };
              const d = ra?.deforestation;
              if (!d) return null;
              if (d.skipped) {
                return (
                  <>
                    <Descriptions.Item label={t("compliance.passport.deforestationSection")}>
                      {t("compliance.passport.deforestationSkipped")}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("compliance.passport.deforestationCutoff")}>
                      {d.cutoff_date ?? t("common.dash")}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("compliance.passport.deforestationMode")}>
                      {d.mode ?? t("common.dash")}
                    </Descriptions.Item>
                  </>
                );
              }
              return (
                <>
                  <Descriptions.Item label={t("compliance.passport.deforestationSection")}>
                    {t("compliance.passport.deforestationAssessed")}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("compliance.passport.deforestationProvider")}>
                    {d.interim || d.mode === "mock"
                      ? t("compliance.passport.deforestationDemoLabel", {
                          provider: d.provider ?? "stub",
                          mode: d.mode ?? "interim",
                        })
                      : String(d.provider ?? "")}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("compliance.passport.deforestationMode")}>
                    {d.mode === "mock"
                      ? t("compliance.passport.deforestationModeDemo", { mode: d.mode })
                      : d.mode ?? t("common.dash")}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("compliance.passport.deforestationCutoff")}>
                    {d.cutoff_date ?? t("common.dash")}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("compliance.passport.deforestationCoefficient")}>
                    {d.coefficient != null
                      ? `${d.coefficient.toFixed(4)} (${d.risk_class ?? "—"})`
                      : t("common.dash")}
                  </Descriptions.Item>
                  {d.combine_rule && (
                    <Descriptions.Item label={t("compliance.deforestationComparison.combineRuleLabel")}>
                      {t(`compliance.deforestationComparison.rules.${d.combine_rule}`, {
                        defaultValue: d.combine_rule,
                      })}
                    </Descriptions.Item>
                  )}
                  {d.disclaimer && (
                    <Descriptions.Item label={t("compliance.passport.deforestationDisclaimer")}>
                      {d.provider === "stub"
                        ? t("compliance.passport.deforestationDisclaimerText")
                        : d.disclaimer}
                    </Descriptions.Item>
                  )}
                </>
              );
            })()}
            <Descriptions.Item label={t("compliance.passport.docsManifestTitle")}>
              {(() => {
                const manifest = (preview.document_manifest as unknown[]) || [];
                if (!manifest.length) return `0 ${t("compliance.passport.filesInManifest")}`;
                const cats = manifest
                  .map((m) =>
                    typeof m === "object" && m && "category" in (m as object)
                      ? String((m as { category?: string }).category)
                      : null
                  )
                  .filter(Boolean);
                return `${manifest.length} ${t("compliance.passport.filesInManifest")}${
                  cats.length ? `: ${cats.join(", ")}` : ""
                }`;
              })()}
            </Descriptions.Item>
          </Descriptions>
          {(() => {
            const ra = preview.risk_assessment as { deforestation?: DeforestationAssessment };
            const d = ra?.deforestation;
            if (!d || d.skipped) return null;
            return <DeforestationComparisonPanel deforestation={d} />;
          })()}
          <Button
            type="link"
            loading={downloadLoading}
            onClick={async () => {
              setDownloadLoading(true);
              try {
                await downloadCompliancePassport(caseData.id);
              } catch {
                message.error(t("compliance.passport.downloadFailed"));
              } finally {
                setDownloadLoading(false);
              }
            }}
          >
            {t("compliance.passport.downloadZip")}
          </Button>
        </>
      )}
      {versions.length > 0 && (
        <>
          <Space wrap style={{ width: "100%", justifyContent: "space-between" }}>
            <Typography.Title level={5} style={{ marginBottom: 0 }}>
              {t("compliance.passport.versionHistory")}
            </Typography.Title>
            <PassportVersionCompare versions={versions} />
          </Space>
          {isNarrow ? (
            <MobileDataCards
              testId="passport-versions-mobile"
              items={versions}
              rowKey={(row) => row.id}
              title={(row) => `v${row.version}`}
              fields={(row) => [
                {
                  label: t("compliance.passport.generatedAt"),
                  value: new Date(row.generated_at).toLocaleString(),
                },
              ]}
              actions={(row) => (
                <Button
                  type="link"
                  className="dds-touch-target"
                  loading={versionDownloading === row.version}
                  onClick={async () => {
                    setVersionDownloading(row.version);
                    try {
                      await downloadCompliancePassportVersion(caseData.id, row.version);
                    } catch {
                      message.error(t("compliance.passport.downloadFailed"));
                    } finally {
                      setVersionDownloading(null);
                    }
                  }}
                >
                  {t("common.download")}
                </Button>
              )}
            />
          ) : (
          <Table            size="small"
            rowKey="id"
            pagination={false}
            dataSource={versions}
            columns={[
              {
                title: t("compliance.passport.version"),
                dataIndex: "version",
                render: (v: number) => `v${v}`,
              },
              {
                title: t("compliance.passport.generatedAt"),
                dataIndex: "generated_at",
                render: (v: string) => new Date(v).toLocaleString(),
              },
              {
                title: t("compliance.columns.actions"),
                key: "actions",
                render: (_, row) => (
                  <Button
                    type="link"
                    loading={versionDownloading === row.version}
                    onClick={async () => {
                      setVersionDownloading(row.version);
                      try {
                        await downloadCompliancePassportVersion(caseData.id, row.version);
                      } catch {
                        message.error(t("compliance.passport.downloadFailed"));
                      } finally {
                        setVersionDownloading(null);
                      }
                    }}
                  >
                    {t("common.download")}
                  </Button>
                ),
              },
            ]}
          />
          )}
        </>
      )}
    </Space>
  );
}
